import React, { useState, useEffect } from 'react';
import Container from '../../layout/Container';
import Button from '../../common/Button';

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contato');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Imagem de fundo com parallax */}
      <div 
        className="absolute inset-0 transition-transform duration-300 ease-out"
        style={{
          transform: `translate(${mousePosition.x}px, ${mousePosition.y}px) scale(1.1)`,
        }}
      >
        <img 
          src="/images/hero/background.jpg" 
          alt="Background" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950/90 via-gray-900/80 to-gray-950/90"></div>
      </div>

      {/* Formas geométricas animadas - escondidas em mobile para performance */}
      <div className="absolute inset-0 hidden md:block">
        <div 
          className="absolute top-20 right-20 w-64 lg:w-96 h-64 lg:h-96 border border-emerald-500/10 rotate-45 transform transition-all duration-700 animate-spin-slow"
          style={{
            transform: `rotate(${45 + mousePosition.x}deg) scale(${1 + mousePosition.y * 0.01})`,
          }}
        />
        <div 
          className="absolute top-40 right-40 w-48 lg:w-64 h-48 lg:h-64 border border-emerald-500/10 rotate-45 transform transition-all duration-500 animate-pulse-slow"
          style={{
            transform: `rotate(${-45 + mousePosition.y}deg)`,
          }}
        />
      </div>

      {/* Glows com movimento - ajustados para mobile */}
      <div 
        className="absolute w-80 h-80 md:w-96 md:h-96 lg:w-[600px] lg:h-[600px] bg-emerald-500/10 rounded-full blur-3xl transition-all duration-1000"
        style={{
          top: '25%',
          left: '15%',
          transform: `translate(${mousePosition.x * 2}px, ${mousePosition.y * 2}px)`,
        }}
      />
      <div 
        className="absolute w-80 h-80 md:w-96 md:h-96 lg:w-[600px] lg:h-[600px] bg-emerald-500/5 rounded-full blur-3xl transition-all duration-1000"
        style={{
          bottom: '25%',
          right: '15%',
          transform: `translate(${-mousePosition.x * 2}px, ${-mousePosition.y * 2}px)`,
        }}
      />

      <Container className="relative z-10">
        <div className="text-center max-w-5xl mx-auto pt-28 sm:pt-32 md:pt-36 lg:pt-40 pb-12 md:pb-16 px-4 sm:px-6">
          {/* Headline */}
          <h1 
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight sm:leading-snug md:leading-relaxed mb-8 md:mb-10 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="inline-block animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Transformamos ideia em software.
            </span>
            <br />
            <span 
              className="text-emerald-500 inline-block animate-fade-in-up"
              style={{ animationDelay: '0.3s' }}
            >
              E software em resultado
            </span>
          </h1>

          {/* Subheadline com hover underlines */}
          <p 
            className={`text-sm sm:text-base md:text-lg lg:text-xl text-gray-400 mb-12 sm:mb-14 md:mb-16 max-w-3xl mx-auto leading-relaxed sm:leading-loose transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-10 blur-sm'
            }`}
          >
            Somos a fábrica digital que{' '}
            <span className="text-white font-semibold relative inline-block group">
              tira projetos do papel com agilidade
              <span className="hidden sm:inline absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-500 group-hover:w-full transition-all duration-500"></span>
            </span>,
            estratégia e identidade. Seja{' '}
            <span className="text-white font-semibold relative inline-block group">
              um sistema, um app ou uma plataforma
              <span className="hidden sm:inline absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-500 group-hover:w-full transition-all duration-500"></span>
            </span>
            : a gente resolve.
          </p>

          {/* CTA Button - responsivo e funcional */}
          <div 
            className={`flex justify-center mb-16 sm:mb-20 md:mb-24 transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
            }`}
          >
            <div className="relative group">
              <div className="absolute -inset-1 sm:-inset-2 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full opacity-30 group-hover:opacity-60 blur-lg sm:blur-xl transition-all duration-500 animate-pulse-slow"></div>
              
              <Button 
                variant="primary" 
                size="lg"
                onClick={scrollToContact}
                className="relative transform transition-all duration-300 hover:scale-105 active:scale-95 hover:-translate-y-1 shadow-2xl shadow-emerald-500/50 text-sm sm:text-base md:text-lg px-6 sm:px-8 py-3 sm:py-4"
              >
                <span className="hidden sm:inline">Quero construir com vocês</span>
                <span className="inline sm:hidden">Vamos construir</span>
                <svg 
                  className="w-4 h-4 sm:w-5 sm:h-5 ml-2 transition-transform duration-300 group-hover:translate-x-2" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </div>
          </div>

          {/* Scroll Indicator - escondido em mobile pequeno */}
          <div className="hidden sm:block animate-bounce-slow relative">
            <div className="absolute inset-0 blur-md">
              <svg 
                className="w-5 h-5 sm:w-6 sm:h-6 mx-auto text-emerald-500 opacity-50" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
            <svg 
              className="w-5 h-5 sm:w-6 sm:h-6 mx-auto text-emerald-500 relative" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Hero;